'use client';

import Image from 'next/image';
import { Check, Gift, Lock } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import {
  ActivateTrialButton,
  SignedInFooter,
  type ActivationState
} from './activation-ui';
import type { ActivationSummaryResponseDto } from '@/lib/services/billing';

/**
 * Activation page — variant 2 (LF-4269 pilot → LF-4086 permanent).
 *
 * The data-driven "Welcome to Vale" activation summary shown to every gated firm.
 * All figures come from the BE activation-summary (`summary` prop): the firm's
 * name, derived plan/tier, prices, welcome gift and projected dates. The page
 * does NO pricing or GST maths — amounts are EX-GST with "+ GST" suffixes.
 *
 * Status-aware: a `pending` firm sees trial framing ("Plan starts {date}",
 * "NIL charged today"); an `active-free` firm sees immediate framing ("Plan
 * starts today", "Due today") — there is no free trial on that path.
 *
 * The "Due" line shows the BE-provided first-charge total: GST-inclusive ("incl.
 * GST") when GST is enabled (LF-4369), ex-GST ("+ GST") otherwise — so it always
 * matches what is actually charged. The "Add payment details to get started" CTA
 * drives the real Stripe checkout via the gate's start-trial mutation.
 */

/** Vale Terms & Conditions (same document the Stripe checkout references). */
const TERMS_URL = 'https://www.vale.legal/terms-and-conditions.pdf';

/**
 * At or below this many days to the first charge, show the charge amount
 * directly instead of the "NIL now, charged later" trial framing.
 */
const NIL_DUE_MIN_TRIAL_DAYS = 6;

const SUBSCRIPTION_FEATURES = [
  'AI scribe for meetings',
  'AI document analysis',
  'Integrated client intake forms',
  'Shared notes'
];

const MATTER_FEATURES = [
  'Discover estate assets',
  'Auto-populate asset redemption forms',
  'Identify beneficiaries online',
  'Verify beneficiary banking details',
  'Calculate estate entitlements',
  'Custom document and email templates'
];

/** Format EX-GST cents as "A$129" / "A$1,500.50" (drops .00 on whole dollars). */
function formatAud(cents: number): string {
  const dollars = cents / 100;
  const hasFraction = cents % 100 !== 0;
  return `A$${dollars.toLocaleString('en-AU', {
    minimumFractionDigits: hasFraction ? 2 : 0,
    maximumFractionDigits: 2
  })}`;
}

const WEEKDAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday'
];
const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];

/** "Friday 17 July 2026" — built from local-time getters (deterministic). */
function formatPlanDate(iso: string): string {
  const d = new Date(iso);
  return `${WEEKDAYS[d.getDay()]} ${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

/**
 * Whole days from now until an ISO date. Rounded because the BE sets the
 * first-charge date to `now + N×24h`, so rounding recovers the exact trial-day
 * count N despite the sub-second gap between the BE computing it and this render.
 */
function daysFromNow(iso: string): number {
  const MS_PER_DAY = 24 * 60 * 60 * 1000;
  return Math.round((new Date(iso).getTime() - Date.now()) / MS_PER_DAY);
}

/**
 * Today's date, formatted like {@link formatPlanDate}. Isolated in a helper so
 * the non-deterministic clock read stays out of the component render body.
 */
function formatToday(): string {
  return formatPlanDate(new Date().toISOString());
}

interface ActivationV2LandingProps {
  userEmail: string;
  /** Resolved per-firm data from GET /account/billing/activation-summary. */
  summary: ActivationSummaryResponseDto;
  /** Drives the CTA: idle → redirecting (to Stripe). */
  state: ActivationState;
  onActivate: () => void;
  errorMessage?: string;
}

function FeatureList({ items }: { items: string[] }) {
  return (
    <ul className="mt-3 space-y-2">
      {items.map((item) => (
        <li key={item} className="flex items-start gap-2">
          <Check className="text-success-subtle-foreground mt-0.5 h-4 w-4 shrink-0" />
          <span className="text-foreground-secondary text-sm leading-snug">
            {item}
          </span>
        </li>
      ))}
    </ul>
  );
}

export function ActivationV2Landing({
  userEmail,
  summary,
  state,
  onActivate,
  errorMessage
}: ActivationV2LandingProps) {
  const isActiveFree = summary.membershipStatus === 'active-free';
  const subscriptionPrice = formatAud(summary.monthlySubscriptionCents);
  const firstChargeAmount = formatAud(summary.firstChargeCents);
  const licenceLabel = `${summary.seatCount} ${summary.seatCount === 1 ? 'licence' : 'licences'}`;
  const billingStartsValue = isActiveFree
    ? 'Today'
    : formatPlanDate(summary.planStartDate);
  const trialDaysLength = daysFromNow(summary.firstChargeDate) - 1; // days from today to firstChargeDate
  const showNilDueNow = trialDaysLength > NIL_DUE_MIN_TRIAL_DAYS; // "Due {date} - NIL" vs "Due {date} - $XX"
  const todayLabel = formatToday();
  const firstChargeDateLabel = formatPlanDate(summary.firstChargeDate);
  const isChargeTodaysDate = todayLabel === firstChargeDateLabel;
  const dueLabel = isActiveFree ? 'Due today' : `Due ${firstChargeDateLabel}`;

  return (
    <div className="bg-surface-subtle flex min-h-screen flex-col items-center justify-center px-6 py-12">
      <Image
        src="/ValeBlackLogo.svg"
        alt="Vale"
        width={40}
        height={40}
        priority
      />

      <div className="border-border bg-card mt-8 w-full max-w-lg rounded-xl border shadow-sm">
        <div className="border-border-subtle border-b p-6">
          <h1 className="text-foreground text-xl font-semibold tracking-tight">
            Welcome to Vale
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            {summary.firmName} · {summary.planLabel} plan
          </p>
        </div>

        <div className="space-y-6 p-6">
          {/* Subscription */}
          <div>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-foreground text-sm font-medium">
                  {summary.planLabel} plan – full platform access
                </p>
                <p className="text-muted-foreground mt-0.5 text-xs">
                  {summary.seatCount > 0
                    ? `${licenceLabel} · billed monthly in advance`
                    : 'Billed monthly in advance'}
                </p>
              </div>
              <div className="shrink-0 text-right">
                <p className="text-foreground text-sm font-semibold">
                  {subscriptionPrice} / mo
                </p>
                <p className="text-muted-foreground mt-0.5 text-xs">+ GST</p>
              </div>
            </div>
            <p className="text-muted-foreground mt-4 text-xs font-medium tracking-wide uppercase">
              Included in your subscription
            </p>
            <FeatureList items={SUBSCRIPTION_FEATURES} />
          </div>

          <Separator />

          {/* Per matter */}
          <div>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-foreground text-sm font-medium">
                  Per matter
                </p>
                <p className="text-muted-foreground mt-0.5 text-xs">
                  billed monthly in arrears
                </p>
              </div>
              <div className="shrink-0 text-right">
                <p className="text-foreground text-sm font-semibold">
                  {formatAud(summary.perMatterFeeCents)}
                </p>
                <p className="text-muted-foreground mt-0.5 text-xs">
                  + GST each
                </p>
              </div>
            </div>
            <p className="text-muted-foreground mt-4 text-xs font-medium tracking-wide uppercase">
              Included in each matter
            </p>
            <FeatureList items={MATTER_FEATURES} />
          </div>

          {/* The welcome gift — shown only when a complimentary allowance is set */}
          {summary.gift && (
            <div className="bg-success-subtle flex items-start gap-3 rounded-lg p-4">
              <Gift className="text-success-subtle-foreground mt-0.5 h-4 w-4 shrink-0" />
              <div>
                <p className="text-success-subtle-foreground text-sm font-medium">
                  A gift from Vale to help you get started
                </p>
                <p className="text-success-subtle-foreground mt-0.5 text-sm leading-snug">
                  Your first {summary.gift.matterCount}{' '}
                  {summary.gift.matterCount === 1 ? 'matter is' : 'matters are'}{' '}
                  free – valued at {formatAud(summary.gift.valueCents)} + GST.
                </p>
              </div>
            </div>
          )}

          {/* Plan timing */}
          <div className="bg-surface-subtle rounded-lg p-4">
            <div className="flex items-baseline justify-between">
              <p className="text-muted-foreground text-sm">Billing starts</p>
              <p className="text-foreground text-sm font-medium">
                {billingStartsValue}
              </p>
            </div>
            <div className="mt-2 flex items-baseline justify-between">
              <p className="text-muted-foreground text-sm">Billing cycle</p>
              <p className="text-foreground text-sm font-medium capitalize">
                {summary.billingCycle}
              </p>
            </div>
            <Separator className="my-3" />
            <p className="text-muted-foreground text-xs">
              Review{' '}
              <a
                href={TERMS_URL}
                target="_blank"
                rel="noreferrer"
                className="hover:text-foreground-secondary underline underline-offset-2"
              >
                Vale Terms & Conditions
              </a>{' '}
              for more details.
            </p>
          </div>

          <div className="mb-2">
            {!isActiveFree && showNilDueNow && summary.hasFreeTrial && (
              <p className="text-foreground text-sm font-semibold">
                Add payment information to start your {trialDaysLength} day
                trial
              </p>
            )}
          </div>

          {/* Due — the first-charge total. GST-inclusive when GST is enabled
              (LF-4369), ex-GST with "+ GST" otherwise; the BE decides via
              `gstIncluded` so the figure always matches what is actually charged. */}
          {!showNilDueNow ? (
            <div className="flex items-baseline justify-between gap-4 pt-1">
              <div>
                <p className="text-foreground text-sm font-semibold">
                  {dueLabel}
                </p>
                {!isActiveFree && !isChargeTodaysDate && (
                  <p className="text-muted-foreground mt-0.5 text-xs">
                    NIL charged today.
                  </p>
                )}
              </div>
              <div className="shrink-0 text-right">
                <p className="text-foreground text-lg font-semibold">
                  {firstChargeAmount}
                </p>
                <p className="text-muted-foreground text-xs">
                  {summary.gstIncluded ? 'incl. GST' : '+ GST'}
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-baseline justify-between gap-4 pt-1">
              <div>
                <p className="text-foreground text-sm font-semibold">
                  Due {todayLabel}
                </p>
                {!isActiveFree && (
                  <p className="text-muted-foreground mt-0.5 text-xs">
                    {firstChargeAmount}{' '}
                    {summary.gstIncluded ? 'incl. GST' : '+ GST'} will be{' '}
                    charged on {firstChargeDateLabel}
                  </p>
                )}
              </div>
              <div className="shrink-0 text-right">
                <p className="text-foreground text-lg font-semibold">NIL</p>
              </div>
            </div>
          )}

          <ActivateTrialButton
            state={state}
            onActivate={onActivate}
            label="Add payment details to get started"
            className="w-full"
          />
          {!!errorMessage && (
            <p className="text-error-subtle-foreground text-center text-xs">
              {errorMessage}
            </p>
          )}

          <div className="text-muted-foreground flex items-center justify-center gap-1 text-xs">
            <Lock className="h-3 w-3" />
            Secure checkout by Stripe
          </div>
        </div>
      </div>

      <SignedInFooter email={userEmail} className="mt-6" />
      <p className="text-muted-foreground mt-2 text-xs">
        &copy; Vale Innovations 2026
      </p>
    </div>
  );
}
